/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Needed for redux-saga es6 generator support
import "@babel/polyfill";
import cssVars from "css-vars-ponyfill";
import "bootstrap/dist/css/bootstrap.min.css";

// Initialize Sentry as early as possible
import { initSentry, updateSentryContext } from './utils/sentry';

// Sentry configuration initialization happens in initSentry()

// Initialize Sentry
initSentry();

// Import all the third party stuff
import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { Router, browserHistory } from "react-router";
import { syncHistoryWithStore } from "react-router-redux";

import jwtDecode from "jwt-decode";
import "sanitize.css/sanitize.css";

import configureStore from "./store";

// Import CSS reset and Global Styles
import "./global-styles";

import "../tracing";

// Import root routes
import createRootComponent from "./routes";
import { czKey, personalUrl, websiteUrl } from "./utils/env";
cssVars();

// const runtime = require('offline-plugin/runtime'); // eslint-disable-line global-require

// Export the store to give access to non component files. Will need change if moving to ServerSideRendering
// more info: https://daveceddia.com/access-redux-store-outside-react/#option-1-export-the-store
// Create redux store with history
// this uses the singleton browserHistory provided by react-router
// Optionally, this could be changed to leverage a created history
// e.g. `const browserHistory = useRouterHistory(createBrowserHistory)();`
const initialState = {};
export const store = configureStore(initialState, browserHistory);

// Sync history and store, as the react-router-redux reducer
// is under the non-default key ("routing"), selectLocationState
// must be provided for resolving how to retrieve the "route" in the state
const selectLocationState = () => {
  let prevRoutingState;
  let prevRoutingStateJS;

  return (state) => {
    const routingState = state.get("route"); // or state.route

    if (!routingState.equals(prevRoutingState)) {
      prevRoutingState = routingState;
      prevRoutingStateJS = routingState.toJS();
    }

    return prevRoutingStateJS;
  };
};

const history = syncHistoryWithStore(browserHistory, store, {
  selectLocationState: selectLocationState(),
});

history.listen(() => {
  if (localStorage.getItem("token")) {
    const { pathname } = history.getCurrentLocation();
    // Add proper null checks to prevent 'Cannot read properties of undefined' error
    
    const userData = store.getState().get("App")?.get("userData");
    const jwtInfo = jwtDecode(localStorage.getItem("token"));
    
    // Update Sentry context with data from Redux store (more reliable than JWT)
    if (userData) {
      updateSentryContext(userData);
    }
      
    if (jwtInfo) {
      const logPageVisit = async () => {
        const formData = {
          pageUrl: pathname,
          userAccountId: jwtInfo.sub,
        };
        try {
          await fetch(`${personalUrl}pageVisits`, {
            method: "POST",
            headers: {
              Authorization: localStorage.getItem("token"),
            },
            body: JSON.stringify(formData),
          });
        } catch (error) {
          return false;
        }
        return true;
      };
      logPageVisit();
    }
  }
});

// Set up the router, wrapping all Routes in the App component
const rootRoute = createRootComponent(store);

//  ChurnZero init
window.onload = () => {
  window.ChurnZero.push(["setAppKey", czKey]);
};

const render = () => {
  window.analytics.load(
    process.env.NODE_ENV === "production"
      ? "k3rjVFuIRl6MK8emqtsPi7AK4fKfspzr"
      : "rl9kzxpZ4OuR7tJVC9KA995TFvlbc0EB"
  );
  ReactDOM.render(
    <Provider store={store}>
      <Router history={history} routes={rootRoute} />
    </Provider>,
    document.getElementById("app")
  );
};

// Call the render function
render();

const onClearCache = () => {
  if (caches) {
    caches.keys().then((names) => names.forEach((name) => caches.delete(name)));
  }
};

const isLocalEnvironment = new URL(websiteUrl).hostname === "localhost";

// Load the service worker to refresh the page and clear the cache when a new version is available
if ("serviceWorker" in navigator && !isLocalEnvironment) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((registration) => {
        // Listen for updates to the service worker.
        registration.addEventListener("updatefound", () => {
          const installingWorker = registration.installing;
          installingWorker.onstatechange = () => {
            // When the new Service Worker is installed.
            if (
              installingWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              onClearCache();
              window.location.reload();
            }
          };
        });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error("Error during service worker registration:", error);
      });
  });
}

// Install ServiceWorker and AppCache in the end since
// it's not most important operation and if main code fails,
// we do not want it installed
/* eslint-disable no-console */
// sessionStorage.removeItem('installingUpdate');
// if (process.env.NODE_ENV === 'production') {
//   runtime.install({
//     onUpdating: () => {
//       sessionStorage.setItem('installingUpdate', true);
//       console.log('SW Event:', 'onUpdating');
//       localStorage.setItem('installingUpdate', true);
//     },
//     onUpdateReady: () => {
//       console.log('SW Event:', 'onUpdateReady');
//       // Tells to new SW to take control immediately
//       runtime.applyUpdate();
//     },
//     onUpdated: () => {
//       console.log('SW Event:', 'onUpdated');
//       // Reload the webpage to load into the new version
//       sessionStorage.setItem('installingUpdate', false);
//       sessionStorage.removeItem('installingUpdate');
//       window.location.reload();
//     },
//     onUpdateFailed: () => {
//       console.log('SW Event:', 'onUpdateFailed');
//       sessionStorage.setItem('installingUpdate', false);
//     }
//   });
// }
